import { Button, ButtonGroup, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Input, Slider, Tooltip } from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Clipboard, Ellipsis, Pencil, RotateCcw, Save, Trash, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useLocalStorage } from 'usehooks-ts'
import { $fetch } from 'utils/fetch'
import ConfirmDeleteModal from '../Analysis/ConfirmDeleteModal'
import EffectsTimeline from './EffectsTimeline'
import GenerationHistory from './GenerationHistory'
import ReadScriptButton from './ReadScriptButton'
import TaggedTextEditor from './TaggedTextEditor'
import TalkToVoiceButton from './TalkToVoiceButton'
import { supportedEffects } from './utils'

export default function TestScriptEditor({ selectedClone, setSelectedCloneId }) {
  const [testScript, setTestScript] = useLocalStorage('testScript', 'Hey it\'s Blandie. How are you doing today?')
  const [expressiveness, setExpressiveness] = useState(selectedClone.expressiveness ?? 0.5)
  const [consistency, setConsistency] = useState(selectedClone.consistency ?? 0.5)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState(selectedClone.name)
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    setExpressiveness(selectedClone.expressiveness ?? 0.5)
    setConsistency(selectedClone.consistency ?? 0.5)
    setNewName(selectedClone.name)
  }, [selectedClone])

  const isDirty = useMemo(() => {
    return expressiveness !== selectedClone.expressiveness || consistency !== selectedClone.consistency
  }, [expressiveness, consistency, selectedClone])

  const { mutate: updateConfig, isPending: isUpdatingConfig } = useMutation({
    mutationFn: () => $fetch(`v1/voices/${selectedClone.id}/config`, {
      method: 'PATCH',
      body: { expressiveness, consistency },
    }),
    onSuccess: () => {
      setShowConfirmation(true)
      setTimeout(() => {
        setShowConfirmation(false)
      }, 3000)
      queryClient.setQueryData(['voices'], old => old.map(v => v.id === selectedClone.id ? { ...v, expressiveness, consistency } : v))
    },
    onError: () => {
      toast.error('Failed to update voice config')
    },
  })

  const handleResetConfig = () => {
    setExpressiveness(selectedClone.expressiveness)
    setConsistency(selectedClone.consistency)
  }

  const handleMenuAction = (key) => {
    if (key === 'copy') {
      navigator.clipboard.writeText(selectedClone.id)
      toast.success('Voice ID copied to clipboard')
    }
    else if (key === 'rename') {
      setIsRenaming(true)
    }
    else if (key === 'delete') {
      setIsConfirmDeleteModalOpen(true)
    }
  }

  const { mutate: renameVoice, isPending: isRenamingVoice } = useMutation({
    mutationFn: () => $fetch(`v1/voices/${selectedClone.id}/rename`, {
      method: 'PATCH',
      body: { name: newName },
    }),
    onSuccess: () => {
      toast.success('Voice successfully renamed')
      queryClient.setQueryData(['voices'], old => old.map(v => v.id === selectedClone.id ? { ...v, name: newName } : v))
      setIsRenaming(false)
    },
    onError: () => {
      toast.error('Failed to rename voice')
    },
  })

  const { mutate: deleteVoice, isPending: isDeletingVoice } = useMutation({
    mutationFn: () => $fetch(`v1/voices/${selectedClone.id}`, {
      method: 'DELETE',
    }),
    onSuccess: () => {
      toast.success('Voice successfully deleted')
      setSelectedCloneId(null)
      queryClient.setQueryData(['voices'], old => old.filter(v => v.id !== selectedClone.id))
    },
    onError: () => {
      toast.error('Failed to delete voice')
    },
    onSettled: () => {
      setIsConfirmDeleteModalOpen(false)
    },
  })

  return (
    <div className="w-full flex flex-col gap-6 p-6">
      <div className="flex flex-col border-b border-gray-200 pb-4 items-start">
        <div className="flex items-start gap-2 w-full mb-2">
          <div className="flex gap-2 items-center mr-auto">
            {isRenaming
              ? (
                  <>
                    <Input
                      variant="underlined"
                      placeholder="Enter new name"
                      value={newName}
                      onValueChange={setNewName}
                      classNames={{
                        input: 'text-xl font-bold tracking-tight',
                      }}
                    />
                    <ButtonGroup>
                      <Button
                        variant="light"
                        isIconOnly
                        color="default"
                        radius="sm"
                        size="sm"
                        onPress={() => setIsRenaming(false)}
                      >
                        <X className="text-gray-400" size={16} />
                      </Button>
                      <Button
                        variant="light"
                        isIconOnly
                        color="success"
                        radius="sm"
                        size="sm"
                        onPress={renameVoice}
                        isLoading={isRenamingVoice}
                      >
                        <Check size={16} />
                      </Button>
                    </ButtonGroup>
                  </>
                )
              : (
                  <>
                    <h3 className="tracking-tight text-xl font-bold">{selectedClone.name}</h3>

                    <Dropdown
                      placement="right-start"
                      classNames={{
                        content: 'rounded-sm',
                      }}
                    >
                      <DropdownTrigger>
                        <Button
                          isIconOnly
                          variant="light"
                          color="default"
                          radius="sm"
                          size="sm"
                        >
                          <Ellipsis size={16} />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Static Actions" onAction={handleMenuAction}>
                        <DropdownItem startContent={<Clipboard size={16} className="text-gray-400" />} key="copy">Copy ID</DropdownItem>
                        <DropdownItem startContent={<Pencil size={16} className="text-gray-400" />} key="rename">Rename Voice</DropdownItem>
                        <DropdownItem startContent={<Trash size={16} className="text-danger-300" />} color="danger" className="text-danger-500" key="delete">Delete Voice</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </>
                )}
          </div>
          <TalkToVoiceButton name={selectedClone.name} id={selectedClone.id} />
          <ReadScriptButton expressiveness={expressiveness} consistency={consistency} />
        </div>
        <p className="text-sm font-light text-gray-500">
          {selectedClone.description
            ? (
                selectedClone.description
              )
            : (
                <>
                  Try different test scripts and experiment with effect tags to find the perfect voice settings.
                  <br />
                  Adjust the Expressiveness and Consistency sliders to fine-tune how natural and consistent your voice clone sounds.
                </>
              )}
        </p>
      </div>

      <TaggedTextEditor
        id="test-script-textarea"
        value={testScript}
        onValueChange={setTestScript}
        radius="sm"
        label="Test Script"
        variant="bordered"
        placeholder="Enter text to be synthesized with your voice clone..."
        supportedEffects={supportedEffects}
        description={(
          <div className="flex justify-between">
            <p className="text-xs text-gray-500">
              Type
              {' '}
              <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-2xs">&lt;</kbd>
              {' '}
              to insert effects
            </p>
            <p className="text-xs text-gray-500">
              {testScript.length}
              {' '}
              characters
            </p>
          </div>
        )}
      />

      <EffectsTimeline text={testScript} />

      <div className="flex flex-row rounded-sm h-24 border border-gray-200">
        <div className="flex flex-row justify-between w-full items-center p-3 gap-8">
          <div className="flex flex-col w-full gap-1 px-2">
            <Slider
              size="sm"
              minValue={0}
              label="Expressiveness"
              showTooltip
              showSteps
              maxValue={1}
              step={0.1}
              value={expressiveness}
              onChange={setExpressiveness}
              classNames={{
                label: 'font-medium',
                filler: 'bg-gradient-to-r from-secondary-100 to-secondary-300',
                track: 'border-s-secondary-100',
              }}
            />
            <span className="flex justify-between">
              <span className="text-xs text-gray-500">Less Expressive</span>
              <span className="text-xs text-gray-500">More Expressive</span>
            </span>
          </div>

          <div className="flex flex-col w-full gap-1">
            <Slider
              size="sm"
              label="Consistency"
              showTooltip
              minValue={0}
              showSteps
              maxValue={1}
              color="primary"
              step={0.1}
              value={consistency}
              onChange={setConsistency}
              classNames={{
                label: 'font-medium',
                filler: 'bg-gradient-to-r from-primary-100 to-primary-300',
                track: 'border-s-primary-100',
              }}
            />
            <span className="flex justify-between">
              <span className="text-xs text-gray-500">Less Consistent</span>
              <span className="text-xs text-gray-500">More Consistent</span>
            </span>
          </div>
        </div>
        <ButtonGroup isDisabled={!isDirty} className="flex-col border-l border-gray-200 divide-y divide-gray-200">
          <Tooltip isDisabled={!isDirty} content="Reset to default settings">
            <Button
              variant="light"
              isIconOnly
              disableRipple
              size="md"
              color="danger"
              radius="none"
              className="h-full"
              onPress={handleResetConfig}
            >
              <RotateCcw className="size-4" />
            </Button>
          </Tooltip>
          <Tooltip isDisabled={!isDirty} content="Save changes">
            <Button
              variant="light"
              isIconOnly
              disableRipple
              size="md"
              color="success"
              radius="none"
              className="h-full"
              isLoading={isUpdatingConfig}
              onPress={updateConfig}
            >
              {showConfirmation ? <Check className="size-4" /> : <Save className="size-4" />}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </div>

      <GenerationHistory selectedClone={selectedClone} />
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteModalOpen}
        onOpenChange={setIsConfirmDeleteModalOpen}
        onConfirm={deleteVoice}
        isPending={isDeletingVoice}
        title="Delete Voice Clone"
        message="Are you sure you want to delete this voice clone? This action cannot be undone."
      />
    </div>
  )
}
